/* eslint-disable react/require-default-props */
import React, { useMemo } from 'react';
import { Button, FlexRow } from 'design-system';
import { STAR_ICON_FILLED_COLOR, StarIcon } from 'design-system/components/Icons';
import { getOptionId, isSameId } from '../surveyPreviewUtils';

function resolveMaxStars(question) {
  if (question?.maxStarRating) {
    return Number(question.maxStarRating);
  }
  if (question?.options?.length) {
    const values = question.options.map((option) => Number(option.value ?? option.text ?? 0));
    const maxValue = Math.max(...values);
    return maxValue > 0 ? maxValue : 5;
  }
  return 5;
}

export default function RatingOptions({
  options = [],
  question,
  isStarRating = false,
  preview = false,
  value,
  onChange,
  interactive = false,
}) {
  const maxStars = useMemo(() => (isStarRating ? resolveMaxStars(question) : 0), [isStarRating, question]);
  const selectedStars = Number(value) || 0;

  if (isStarRating) {
    return (
      <FlexRow
        wrap
        align="center"
        justify={preview ? 'start' : 'center'}
        fullWidth={false}
        className={preview ? 'cc-preview-star-rating' : 'mt-3'}
        role="group"
        aria-label={`${maxStars} star rating`}
      >
        {Array.from({ length: maxStars }).map((_, index) => {
          const starValue = index + 1;
          const filled = starValue <= selectedStars;

          return (
            <Button
              key={`star-${starValue}`}
              type="button"
              variant="link"
              className={['cc-preview-star-rating__star', filled ? 'cc-preview-star-rating__star--filled' : '']
                .filter(Boolean)
                .join(' ')}
              aria-label={`${starValue} star${starValue === 1 ? '' : 's'}`}
              aria-pressed={filled}
              disabled={!interactive}
              onClick={() => interactive && onChange?.(starValue)}
            >
              <StarIcon
                size={preview ? 24 : 28}
                filled={filled}
                fill={filled ? STAR_ICON_FILLED_COLOR : 'none'}
                stroke={filled ? STAR_ICON_FILLED_COLOR : 'var(--pal-grey-blue-400)'}
              />
            </Button>
          );
        })}
      </FlexRow>
    );
  }

  return (
    <FlexRow
      wrap
      align="center"
      justify={preview ? 'start' : 'center'}
      fullWidth
      className={preview ? 'cc-preview-rating-options' : 'mt-3'}
      role="group"
    >
      {options.map((option, index) => {
        const optionId = getOptionId(option, index);
        const selected = isSameId(value, optionId);

        return (
          <Button
            type="button"
            key={optionId}
            variant="link"
            className={[
              preview ? 'cc-preview-rating-option' : 'rating-option mr-2 mb-2',
              selected ? 'cc-preview-rating-option--selected' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-pressed={selected}
            disabled={!interactive}
            onClick={() => interactive && onChange?.(optionId)}
          >
            {option.text}
          </Button>
        );
      })}
    </FlexRow>
  );
}
